// =====================================================================
// src/ui.js — os poucos utilitários de DOM que todo módulo usa
//
// el()     cria elemento com atributos e filhos (texto vira TextNode: sem
//          innerHTML com dado do banco, nunca)
// icone()  um <svg><use> do sprite do index.html
// modal    um overlay por vez; Esc e clique fora fecham
// aviso    um toast curto, canto de cima
// enquantoAtualiza()  a tela diz quando terminou de se redesenhar
// =====================================================================

export function el(tag, attrs, ...filhos) {
  const e = document.createElement(tag);
  if (attrs) for (const [k, v] of Object.entries(attrs)) {
    if (v === null || v === undefined || v === false) continue;
    if (k === 'class') e.className = v;
    // `dataset: { hoje: c.hoje ? '1' : null }` gravava a STRING "null" — e um
    // seletor `[data-hoje]` casava com tudo (achado no Quadro, 21/09). Nulo
    // e indefinido não entram; `false` também não, como nos atributos.
    else if (k === 'dataset') { for (const [dk, dv] of Object.entries(v || {})) if (dv !== null && dv !== undefined && dv !== false) e.dataset[dk] = dv; }
    else if (k.startsWith('on') && typeof v === 'function') e.addEventListener(k.slice(2), v);
    // `Object.assign(style, ...)` ignora propriedade customizada (`--cor`):
    // ela só entra por setProperty. Sem isto, uma classe do MdM que recebe
    // a cor por variável (`.card-resumo { border-top:4px solid var(--c) }`)
    // sairia com a cor padrão — e sem erro nenhum no console.
    else if (k === 'style' && typeof v === 'object') {
      for (const [p, valor] of Object.entries(v)) {
        if (p.startsWith('--')) e.style.setProperty(p, String(valor));
        else e.style[p] = valor;
      }
    }
    else if (k in e && typeof v === 'boolean') e[k] = v;
    else e.setAttribute(k, v === true ? '' : v);
  }
  for (const f of filhos.flat()) {
    if (f === null || f === undefined || f === false) continue;
    e.appendChild(typeof f === 'string' || typeof f === 'number' ? document.createTextNode(String(f)) : f);
  }
  return e;
}

export function icone(nome, cls = 'ic') {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', cls);
  const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
  use.setAttribute('href', '#' + nome);
  svg.appendChild(use);
  return svg;
}

let overlayAtual = null;
let aoFecharAtual = null;
// `opts.aoFechar` roda quando a modal sai da tela POR QUALQUER CAMINHO:
// botão, Esc, clique fora, ou outra modal tomando o lugar desta. Serve para
// desligar o que a modal ligou — hoje, o fluxo da câmera na intercorrência.
//
// Antes disso, quem precisava reagir ao fechamento escrevia
// `modal.addEventListener('remove', …)`. Não existe evento `remove` no DOM:
// aquele ouvinte nunca foi chamado uma única vez. Estava em governanca.js
// desde a 0008, sem consequência visível porque o mesmo estado era limpo
// noutro lugar — mas era código que parecia fazer algo e não fazia.
export function abrirModal(conteudo, opts = {}) {
  fecharModal();
  const modal = el('div', { class: 'modal', role: 'dialog', 'aria-modal': 'true', id: 'modal' }, conteudo);
  if (opts.largura) modal.style.maxWidth = opts.largura;
  const overlay = el('div', { class: 'modal-overlay', id: 'modal-overlay' }, modal);
  overlay.addEventListener('click', ev => { if (ev.target === overlay && !opts.fixo) fecharModal(); });
  document.body.appendChild(overlay);
  document.addEventListener('keydown', escFecha);
  overlayAtual = overlay;
  aoFecharAtual = typeof opts.aoFechar === 'function' ? opts.aoFechar : null;
  return modal;
}
function escFecha(ev) { if (ev.key === 'Escape') fecharModal(); }
export function fecharModal() {
  if (!overlayAtual) return;
  const aviso = aoFecharAtual;
  overlayAtual.remove(); overlayAtual = null; aoFecharAtual = null;
  document.removeEventListener('keydown', escFecha);
  // Depois de limpar o estado: se `aoFechar` abrir outra modal (é o caso da
  // intercorrência voltando para a ficha), ela não pode ser fechada pela
  // limpeza desta.
  if (aviso) { try { aviso(); } catch (_) { /* fechar nunca falha para quem usa */ } }
}
export function modalAberto() { return !!overlayAtual; }

// ── A BARRA DE ABAS DO MONTEIRO DE MORAES ────────────────────────────
// `_reservasTabBar` (index.html do MdM, linha 2508), palavra por palavra:
// trilho cinza #f3f4f6 com raio 10 e 4px de folga, a ativa em #1B3A7A
// sólido com sombra, as outras transparentes em cinza-médio.
//
// Mora aqui, e não dentro de um módulo, porque é o MESMO componente em toda
// tela que tem abas. Foi essa a lição de 16/09: o acabamento dele não está
// no CSS, está na marcação de cada `render*` — se cada módulo recopiar a
// marcação à mão, elas divergem na terceira tela, que foi exatamente o que
// aconteceu antes.
//
//   abas: [{ chave, rotulo }]      ativa: chave      aoTrocar: chave => {}
export function barraDeAbas(id, abas, ativa, aoTrocar) {
  return el('div', { id, style: { display: 'flex', gap: '4px', marginBottom: '18px', background: '#f3f4f6', borderRadius: '10px', padding: '4px', width: 'fit-content' } },
    abas.map(a => {
      const eAtiva = a.chave === ativa;
      return el('button', {
        type: 'button', dataset: { aba: a.chave },
        onclick: () => aoTrocar(a.chave),
        style: {
          border: 'none', borderRadius: '8px', padding: '7px 18px', fontSize: '13px',
          fontWeight: eAtiva ? '700' : '500', cursor: 'pointer', transition: 'all 0.15s',
          background: eAtiva ? '#1B3A7A' : 'transparent',
          color: eAtiva ? '#fff' : '#6b7280',
          boxShadow: eAtiva ? '0 1px 4px rgba(27,58,122,0.25)' : 'none',
        },
      }, a.rotulo);
    }));
}

// ── A TELA DIZ QUANDO TERMINOU (16/09/2026) ──────────────────────────
// Todo módulo salva assim: `fecharModal(); recarregar();` — e ninguém
// espera pelo `recarregar()`, que é assíncrono. O modal some na hora, as
// chamadas ao banco saem, e só DEPOIS a tela é redesenhada. Entre uma
// coisa e outra ninguém sabia que ainda vinha coisa:
//
//   - a pessoa vê o dado velho, sem sinal nenhum de que algo está a
//     caminho, e numa conexão ruim isso são segundos (e um segundo clique);
//   - as provas de navegador esperavam o modal sumir e já liam a tela, que
//     ainda mostrava o estado anterior. Passavam por sorte: 4 de 10
//     rodadas de `prova_escalas_offline.mjs` falhavam, sempre num assert
//     diferente. Foi o que derrubou o gate #21 — num commit que não
//     encostou em `src/`.
//
// Daqui em diante todo recarregamento passa por aqui, e o <html> conta:
//   data-atualizando   existe enquanto há chamada no ar (é aqui que um dia
//                      pendura o "atualizando…" visível)
//   data-atualizacoes  quantos ciclos JÁ TERMINARAM — monótono
//
// A prova guarda o número antes de clicar e espera ele crescer. Isso é
// determinístico esteja o `recarregar()` onde estiver no meio dos awaits:
// não pergunta "o modal fechou?", pergunta "a tela já se refez depois do
// que eu fiz?". O contador anda mesmo quando o carregamento falha — ele
// marca o fim do ciclo, não o sucesso dele; quem quer saber do erro olha o
// toast, que é o lugar certo.
let atualizacoesEmCurso = 0;
export async function enquantoAtualiza(fn) {
  const h = document.documentElement;
  atualizacoesEmCurso++;
  h.dataset.atualizando = '1';
  try {
    return await fn();
  } finally {
    if (--atualizacoesEmCurso === 0) delete h.dataset.atualizando;
    h.dataset.atualizacoes = String(Number(h.dataset.atualizacoes || 0) + 1);
  }
}

let toastTimer = null;
export function aviso(texto, tipo = 'info') {
  let t = document.getElementById('toast');
  if (!t) { t = el('div', { id: 'toast', role: 'status' }); document.body.appendChild(t); }
  t.textContent = texto;
  t.dataset.tipo = tipo;
  t.classList.add('visivel');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => t.classList.remove('visivel'), tipo === 'erro' ? 6000 : 3000);
}

// Pergunta sim/não numa modal; resolve true/false.
export function confirmar({ titulo, texto, sim = 'Sim', nao = 'Não', perigoso = false }) {
  return new Promise(resolve => {
    const fim = v => { fecharModal(); resolve(v); };
    abrirModal([
      el('h2', { class: 'modal-title', style: { marginBottom: '6px' } }, titulo),
      texto ? el('div', { style: { fontSize: '14px', color: '#374151', marginBottom: '16px' } }, texto) : null,
      el('div', { style: { display: 'flex', gap: '10px' } },
        el('button', { type: 'button', class: 'btn ' + (perigoso ? 'btn-vermelho' : 'btn-verde'), style: { flex: '1' }, onclick: () => fim(true), id: 'confirmar-sim' }, sim),
        el('button', { type: 'button', class: 'btn btn-secondary', style: { flex: '1' }, onclick: () => fim(false), id: 'confirmar-nao' }, nao)),
    ], { fixo: true });
  });
}
